/**
 * 每檔卡片下的「實際成交價輸入 + 股數± + 買進/賣出」工具列。
 * Q5：取代「一鍵採納（預設 entry 不一定買得到）」流程，使用者輸入真實成交價。
 *
 * - 買進：POST /api/thesis/import { symbol, market, shares, avgCost=price, stop?, t1?, t2? }
 *   後端會建一筆 thesis 並 attach 一筆 buy fill；若 symbol 已有 open thesis 會 attach 新 fill。
 * - 賣出：先 GET /api/theses?status=open 找該 symbol 的 open thesis；
 *   POST /api/thesis/{id}/record-exit-fill { price, quantity }。
 *
 * 不接 broker、不自動下單；只是把實際成交價記錄下來給「我的部位」看庫存損益。
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Minus, Plus, Wallet, Calculator } from 'lucide-react';
import { fetchJSON } from '../lib/api-client';
import { isDelistedError } from '../lib/error-utils';
import { ProfitCalc, type TradePlanLevels } from './ProfitCalc';

interface Props {
  symbol: string;
  defaultPrice?: number;
  suggestedStop?: number;
  suggestedT1?: number;
  suggestedT2?: number;
  /** 'tw' or 'us'；省略時依 symbol suffix 判斷 */
  market?: 'tw' | 'us';
  /** 是否 ETF（台股證交稅減半），傳給損益試算 */
  isEtf?: boolean;
  /** 有交易計畫時提供「賣在 T1 / T2 / 停損」快速套用 */
  tradePlan?: TradePlanLevels | null;
}

interface OpenThesis {
  id: string;
  symbol: string;
  status: string;
  remainingShares?: number;
}

interface ThesesResp { theses: OpenThesis[]; count: number }

function inferMarket(symbol: string): 'tw' | 'us' {
  return symbol.endsWith('.TW') || symbol.endsWith('.TWO') ? 'tw' : 'us';
}

function defaultSharesFor(market: 'tw' | 'us'): number {
  return market === 'tw' ? 1000 : 1;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export function PositionEntryRow({ symbol, defaultPrice, suggestedStop, suggestedT1, suggestedT2, market, isEtf = false, tradePlan }: Props) {
  const mk = market ?? inferMarket(symbol);
  const sharesStep = mk === 'tw' ? 1000 : 1;
  const [shares, setShares] = useState<number>(defaultSharesFor(mk));
  const [price, setPrice] = useState<number>(defaultPrice ? round2(defaultPrice) : 0);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [showCalc, setShowCalc] = useState(false);
  const qc = useQueryClient();
  // 只在每檔第一次拿到現價時 seed 一次，避免即時報價每秒覆寫使用者輸入造成數字跳動；要更新請點「現價」。
  const seededRef = useRef<string | null>(null);

  useEffect(() => {
    if (seededRef.current !== symbol && defaultPrice && defaultPrice > 0) {
      setPrice(round2(defaultPrice));
      seededRef.current = symbol;
    }
  }, [symbol, defaultPrice]);

  // 切 symbol 時重設股數預設（避免帶到上一檔），並讓現價重新 seed 一次
  useEffect(() => {
    setShares(defaultSharesFor(mk));
    setMsg(null);
    seededRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const buy = useMutation({
    mutationFn: async () => {
      if (shares <= 0) throw new Error('股數需 > 0');
      if (price <= 0) throw new Error('成交價需 > 0');
      return fetchJSON(`/api/thesis/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          symbol,
          market: mk,
          shares,
          avgCost: price,
          stop: suggestedStop,
          t1: suggestedT1,
          t2: suggestedT2,
          source: 'manual',
          notes: `Q5 直接記錄買進於 ${price}`,
        }),
      });
    },
    onSuccess: () => {
      setMsg({ tone: 'ok', text: `已記錄買進：${shares} ${mk === 'tw' ? '股' : '股'} @ ${price}` });
      qc.invalidateQueries({ queryKey: ['theses'] });
      qc.invalidateQueries({ queryKey: ['theses-open'] });
      qc.invalidateQueries({ queryKey: ['portfolio-theses'] });
    },
    onError: (e: Error) => setMsg({ tone: 'err', text: `買進失敗：${e.message}` }),
  });

  const sell = useMutation({
    mutationFn: async () => {
      if (shares <= 0) throw new Error('股數需 > 0');
      if (price <= 0) throw new Error('成交價需 > 0');
      const resp = await fetchJSON<ThesesResp>(`/api/theses?status=open`);
      const target = resp.theses.find((t) => t.symbol === symbol && (t.remainingShares ?? 0) > 0);
      if (!target) throw new Error('找不到該標的的進行中部位，無法記錄賣出');
      const sellable = target.remainingShares ?? 0;
      if (shares > sellable) throw new Error(`可賣數量僅 ${sellable}，請調整`);
      return fetchJSON(`/api/thesis/${target.id}/record-exit-fill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price, quantity: shares, source: 'manual', note: `Q5 直接記錄賣出於 ${price}` }),
      });
    },
    onSuccess: () => {
      setMsg({ tone: 'ok', text: `已記錄賣出：${shares} 股 @ ${price}` });
      qc.invalidateQueries({ queryKey: ['theses'] });
      qc.invalidateQueries({ queryKey: ['theses-open'] });
      qc.invalidateQueries({ queryKey: ['portfolio-theses'] });
    },
    onError: (e: Error) => {
      if (isDelistedError(e)) setMsg({ tone: 'err', text: '此代號疑似下市，無法寫入賣出' });
      else setMsg({ tone: 'err', text: `賣出失敗：${e.message}` });
    },
  });

  const busy = buy.isPending || sell.isPending;

  return (
    <div className="rounded-xl border-2 border-(--border-strong) bg-gradient-to-br from-(--ice) to-(--bg-card) p-2.5 shadow-[0_3px_10px_rgba(192,106,31,0.12)]">
      <div className="flex items-center gap-1.5 mb-2.5">
        <span className="grid place-items-center w-5 h-5 rounded-full bg-(--accent)/20 text-(--accent-deep)">
          <Wallet size={12} />
        </span>
        <span className="text-[12px] font-extrabold text-(--accent-deep)">記一筆實際成交</span>
        <span className="text-[9px] text-(--text-muted) font-semibold">不接 broker / 不下單 · 只進「我的部位」庫存</span>
      </div>
      <div className="grid grid-cols-2 gap-2 items-end">
        <NumField
          label={mk === 'tw' ? `股數（1 張 = ${sharesStep}）` : '股數'}
          value={shares}
          step={sharesStep}
          min={0}
          onChange={setShares}
        />
        <NumField
          label="實際成交價"
          value={price}
          step={mk === 'tw' && price >= 50 ? 0.1 : 0.01}
          min={0}
          decimals={2}
          onChange={setPrice}
          hint={defaultPrice && defaultPrice > 0 ? defaultPrice.toFixed(2) : undefined}
          onHintClick={() => defaultPrice && setPrice(round2(defaultPrice))}
        />
      </div>
      <div className="mt-2.5 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => buy.mutate()}
          disabled={busy}
          className="h-9 rounded-lg bg-(--up) text-white text-[13px] font-extrabold shadow-[0_2px_6px_rgba(229,86,75,0.35)] hover:brightness-110 active:scale-[.98] disabled:opacity-50 transition"
        >
          {buy.isPending ? '記錄中…' : '▲ 買進'}
        </button>
        <button
          type="button"
          onClick={() => sell.mutate()}
          disabled={busy}
          className="h-9 rounded-lg bg-(--down) text-white text-[13px] font-extrabold shadow-[0_2px_6px_rgba(63,158,124,0.35)] hover:brightness-110 active:scale-[.98] disabled:opacity-50 transition"
          title="只能賣已有部位（找該標的的進行中交易計畫）"
        >
          {sell.isPending ? '記錄中…' : '▼ 賣出'}
        </button>
      </div>
      {msg && (
        <div className={`mt-1.5 text-[10px] ${msg.tone === 'ok' ? 'text-(--up)' : 'text-(--warn)'}`}>
          {msg.text}
        </div>
      )}

      <div className="mt-2.5 border-t-2 border-dashed border-(--border-strong)/50 pt-2">
        <button
          type="button"
          onClick={() => setShowCalc((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-left"
        >
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-(--accent-deep)">
            <Calculator size={12} className="text-(--accent)" />
            損益試算（用上面股數＋成交價當買進）
          </span>
          <span className="text-[10px] font-semibold text-(--text-muted)">{showCalc ? '收起' : '展開'}</span>
        </button>
        {showCalc && (
          <ProfitCalc
            isTW={mk === 'tw'}
            isEtf={isEtf}
            shares={shares}
            entryPrice={price}
            currentPrice={defaultPrice}
            tradePlan={tradePlan}
            symbol={symbol}
          />
        )}
      </div>
    </div>
  );
}

function NumField({ label, value, step, min, onChange, decimals = 0, hint, onHintClick }: {
  label: string;
  value: number;
  step: number;
  min?: number;
  decimals?: number;
  onChange: (v: number) => void;
  hint?: string;
  onHintClick?: () => void;
}) {
  const dec = () => onChange(Math.max(min ?? -Infinity, Number((value - step).toFixed(decimals || 4))));
  const inc = () => onChange(Number((value + step).toFixed(decimals || 4)));
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] text-(--text-muted) flex items-center justify-between gap-1">
        <span>{label}</span>
        {hint && <button type="button" onClick={onHintClick} className="text-[9px] text-(--accent) hover:underline">{hint}</button>}
      </label>
      <div className="flex items-stretch rounded-lg overflow-hidden border-2 border-(--border-strong) bg-white">
        <button type="button" onClick={dec} className="px-2 bg-(--bg-card-hover) text-(--accent-deep) hover:bg-(--frost)" aria-label={`${label} 減少`}>
          <Minus size={12} />
        </button>
        <input
          type="number"
          value={Number.isFinite(value) ? value : 0}
          step={step}
          min={min}
          aria-label={label}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          className="no-spin w-full text-center text-sm font-bold tnum bg-white text-(--text-primary) outline-none px-1"
        />
        <button type="button" onClick={inc} className="px-2 bg-(--bg-card-hover) text-(--accent-deep) hover:bg-(--frost)" aria-label={`${label} 增加`}>
          <Plus size={12} />
        </button>
      </div>
    </div>
  );
}
